/* Chilli Bomba — cart drawer quantity controls

   The drawer is built in snippets/site-footer.liquid and re-renders its own markup
   whenever the cart changes, so this file does not try to own the drawer. It watches
   the drawer body and, after each render, injects a stepper and a Remove button into
   every line item that does not already have one.

   Why a separate file: the drawer's render() lives inside an IIFE in the footer and
   cannot be reached from outside. Patching after render adds the controls without
   rewriting a 33KB template, which is the riskier change.

   IMPORTANT — the drawer's own markup does not print a quantity anywhere, so the
   stepper cannot read the current quantity from the DOM. It is fetched from /cart.js
   after each render. An earlier version assumed 1, which meant pressing minus on a
   line holding 2 removed the whole line. Never infer the quantity; always sync it.

   FREE SHIPPING THRESHOLD. The drawer holds its own copy in site-footer.liquid
   (var FREE) which read 4900 while the real rate was $45, so a $45.99 cart was told
   it was short. CB_FREE_CENTS, published from snippets/cb-drawer-qty.liquid, is the
   authoritative value and this file repaints the bar with it after every render.
   It must match Settings > Shipping and delivery > Domestic > Free Shipping minimum.
*/
(function () {
  'use strict';

  var busy = false;
  var syncing = false;

  function money(cents) {
    return '$' + (Math.abs(cents) / 100).toFixed(2);
  }

  function drawer() {
    return document.querySelector('.cb-drawer');
  }

  function drawerBody() {
    var d = drawer();
    return d ? d.querySelector('.cb-body') : null;
  }

  function threshold() {
    if (window.CB_FREE_CENTS) return window.CB_FREE_CENTS;
    var ship = drawer() ? drawer().querySelector('.cb-ship') : null;
    var v = ship ? parseInt(ship.getAttribute('data-cb-free'), 10) : 0;
    return v || 0;
  }

  /* Repaint the free-shipping line against the authoritative threshold, so the
     customer is never told they are short of a threshold they have already cleared. */
  function fixShipBar(total) {
    var d = drawer();
    var ship = d ? d.querySelector('.cb-ship') : null;
    var t = threshold();
    if (!ship || !t || typeof total !== 'number') return;
    var head = ship.querySelector('p');
    var fill = ship.querySelector('.cb-track i');
    if (!head || !fill) return;
    var left = t - total;
    var pct = Math.min(100, Math.round((total / t) * 100));
    head.innerHTML = left > 0
      ? 'You&rsquo;re ' + money(left) + ' from free shipping'
      : 'Free shipping unlocked';
    fill.style.width = pct + '%';
  }

  /* Write a cart object onto whatever the drawer currently has on screen. */
  function apply(cart) {
    var body = drawerBody();
    if (!body || !cart || !cart.items) return;
    var rows = body.querySelectorAll('.cb-li');
    var i;

    /* Drop rows that no longer exist, back to front so indexes stay valid. */
    for (i = rows.length - 1; i >= cart.items.length; i--) {
      if (rows[i] && rows[i].parentNode) rows[i].parentNode.removeChild(rows[i]);
    }
    rows = body.querySelectorAll('.cb-li');

    for (i = 0; i < rows.length && i < cart.items.length; i++) {
      var item = cart.items[i];
      rows[i].setAttribute('data-cb-line', String(i + 1));
      var qv = rows[i].querySelector('[data-cb-qtyval]');
      if (qv) qv.textContent = item.quantity;
      var pr = rows[i].querySelector('.cb-pr');
      if (pr) pr.textContent = money(item.final_line_price);
    }

    var d = drawer();
    var subs = d ? d.querySelectorAll('.cb-sub span') : [];
    if (subs.length > 1) subs[subs.length - 1].textContent = money(cart.total_price);

    var cnt = document.querySelector('.w-commerce-commercecartopenlinkcount') ||
              document.querySelector('.cart-quantity');
    if (cnt) cnt.textContent = cart.item_count;

    if (!cart.items.length && !body.querySelector('.cb-empty')) {
      var e = document.createElement('div');
      e.className = 'cb-empty font-p5';
      e.textContent = 'Your cart is empty';
      body.appendChild(e);
    }

    fixShipBar(cart.total_price);
  }

  function sync() {
    if (syncing) return;
    syncing = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { apply(cart); })
      .catch(function () {})
      .then(function () { syncing = false; });
  }

  /* True while any stepper is still showing a placeholder instead of a real number. */
  function needsSync() {
    var body = drawerBody();
    if (!body) return false;
    var rows = body.querySelectorAll('.cb-li');
    for (var i = 0; i < rows.length; i++) {
      var v = rows[i].querySelector('[data-cb-qtyval]');
      if (!v || !/^[0-9]+$/.test((v.textContent || '').trim())) return true;
    }
    return false;
  }

  /* Add a stepper to any line item that lacks one. The line index is 1-based and
     follows DOM order, which is the order /cart.js returns, which is what
     /cart/change.js expects for its line parameter. */
  function decorate() {
    var body = drawerBody();
    if (!body) return;
    var rows = body.querySelectorAll('.cb-li');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      row.setAttribute('data-cb-line', String(i + 1));
      if (row.querySelector('.cb-q')) continue;
      var col = row.querySelector('div');
      if (!col) continue;
      var wrap = document.createElement('span');
      wrap.className = 'cb-q';
      wrap.innerHTML =
        '<button type="button" data-cb-step="-1" aria-label="Decrease quantity">&minus;</button>' +
        '<b data-cb-qtyval>&middot;</b>' +
        '<button type="button" data-cb-step="1" aria-label="Increase quantity">+</button>' +
        '<button type="button" data-cb-remove aria-label="Remove this item">Remove</button>';
      col.appendChild(wrap);
    }
    if (needsSync()) sync();
  }

  function change(line, quantity) {
    if (busy) return;
    busy = true;
    var d = drawer();
    if (d) d.classList.add('cb-busy');

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { apply(cart); decorate(); })
      .catch(function () {})
      .then(function () {
        busy = false;
        if (d) d.classList.remove('cb-busy');
      });
  }

  /* Delegated so it survives every re-render the drawer does on its own. */
  document.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    var btn = t.closest('.cb-q button');
    if (!btn) return;
    ev.preventDefault();
    var row = btn.closest('.cb-li');
    if (!row) return;
    var line = parseInt(row.getAttribute('data-cb-line'), 10);
    if (!line) return;

    var valEl = row.querySelector('[data-cb-qtyval]');
    var raw = valEl ? (valEl.textContent || '').trim() : '';
    var isRemove = btn.hasAttribute('data-cb-remove');

    /* Never guess. If the quantity has not synced yet, fetch it and do nothing else. */
    if (!isRemove && !/^[0-9]+$/.test(raw)) { sync(); return; }

    var next = isRemove ? 0 : parseInt(raw, 10) + parseInt(btn.getAttribute('data-cb-step'), 10);
    if (next < 0) next = 0;
    change(line, next);
  });

  /* The drawer replaces .cb-body wholesale on open and after add-to-cart, so watch it,
     and watch the footer too because the subtotal is repainted there. */
  function watch() {
    var d = drawer();
    var body = drawerBody();
    if (!d || !body) return false;
    decorate();
    var obs = new MutationObserver(function () { decorate(); });
    obs.observe(body, { childList: true });
    var foot = d.querySelector('.cb-foot');
    if (foot) obs.observe(foot, { childList: true });
    return true;
  }

  var tries = 0;
  var iv = setInterval(function () {
    if (watch() || ++tries > 60) clearInterval(iv);
  }, 250);
})();
